import { DateTime } from 'luxon';
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import {
  Box,
  CircularProgress,
  colors,
  Container,
  Fab,
  Hidden,
  LabelDisplayedRowsArgs,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {
  Add as IconAddNew,
  LocalLibraryOutlined as IconEmpty
} from '@material-ui/icons';

import { SplashScreen } from '@checkit/react-components';

import { FloatButton } from '../../common/FloatButton';
import TablePaginationActions from '../../common/TablePaginationActions';
import { useListState } from '../../common/useListState';
import {
  withQueryParams,
  WithQueryParamsProps
} from '../../common/WithQueryParams';
import MaterialUiLink from '../../components/MaterialUiLink';
import { useAxiosDispatch } from '../../middleware/axios';
import { setRowsPerPage } from '../../preferences/actions';
import { Preferences } from '../../preferences/state';
import { State } from '../../state';
import { appMessages } from '../../translations';
import {
  clearRecentlyModified,
  getLibraries,
  getLibraryCount
} from '../actions';
import {
  Libraries,
  Library
} from '../state';

import { messages } from './LibrariesList.translations';
import { LibraryActions } from './LibraryActions';
import { LibrarySearch } from './LibrarySearch';

const useStyles = makeStyles((theme: Theme) => ({
  highlight: {
    backgroundColor: colors.yellow['200']
  },
  libraryStatus: {
    borderRadius: '5px',
    display: 'inherit',
    padding: theme.spacing(1),
    textAlign: 'center'
  },
  pending: {
    backgroundColor: colors.amber['100'],
    color: colors.amber['800']
  },
  published: {
    backgroundColor: colors.green['50'],
    color: colors.green['800']
  },
  spacer: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  tableCell: {
    whiteSpace: 'pre-wrap'
  },
  tableRoot: {
    borderColor: theme.palette.divider,
    borderTop: 'solid 1px'
  },
  toolbar: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  },
  unpublished: {
    backgroundColor: colors.grey['200'],
    color: colors.grey['800']
  }
}));

const formatDate: (d: DateTime) => string = d => d.toLocaleString(DateTime.DATETIME_MED);

interface LibraryRowProps {
  /** The library which this row corresponds to. */
  library: Library;
  /** Whether this row should be highlighted (if the library was recently modified). */
  isHighlighted?: boolean;
}
function LibraryRow({ library, isHighlighted = false }: LibraryRowProps) {
  const { formatMessage } = useIntl( );
  const classes = useStyles( );

  const libraryStatus = (function getPublicationStatus( ): { styling: string; message: string } {
    if (!('status' in library.librarySnapshot)) {
      // not throwing an error as this can happen when a new library is created, or the user is
      // navigating to the library listing screen after loading a single library's edit page by
      // url - both the getOne and create/update endpoints don't return snapshot publication status.
      return { styling: classes.unpublished, message: '-' };
    }

    switch (library.librarySnapshot.status) {
    case 'PUBLISHED':
      return { styling: classes.published, message: formatMessage(messages['libraries.list.published']) };
    case 'UNPUBLISHED':
      const hasBeenPublished = library.lastPublished != null;
      return hasBeenPublished
        ? { styling: classes.pending, message: formatMessage(messages['libraries.list.unpublished.pending']) }
        : { styling: classes.unpublished, message: formatMessage(messages['libraries.list.unpublished']) };
    default:
      throw new Error(`Unrecognised snapshot publication status: "${library.librarySnapshot.status}"`);
    }
  })( );

  const lastPublished = library.lastPublished == null
    ? formatMessage(messages['libraries.list.published.never'])
    : formatMessage(messages['libraries.list.published.last'], { date: formatDate(library.lastPublished) });

  return (
    <TableRow key={library.code} className={isHighlighted ? classes.highlight : ''}>

      <TableCell className={classes.tableCell}>{library.name}</TableCell>

      <Hidden mdDown={true}>
        <TableCell className={classes.tableCell}>{library.description}</TableCell>
        <TableCell>{formatDate(library.created)}</TableCell>
        <TableCell>{formatDate(library.updated)}</TableCell>
        <TableCell>
          <Tooltip title={lastPublished}>
            <div className={`${classes.libraryStatus} ${libraryStatus.styling}`}>{libraryStatus.message}</div>
          </Tooltip>
        </TableCell>
      </Hidden>

      <TableCell>
        <LibraryActions library={library} />
      </TableCell>

    </TableRow>
  );
}

type LibrariesListUrlParams = 'filter';
export interface LibrariesListProps extends RouteComponentProps, WithQueryParamsProps<LibrariesListUrlParams> {
  libraries: Libraries;
  preferences: Preferences;
}
export function LibrariesListImpl({
  preferences,
  libraries,
  location,
  urlParams,
  setUrlParam
}: LibrariesListProps) {
  const { formatMessage } = useIntl( );
  const dispatch = useAxiosDispatch( );
  const classes = useStyles( );
  const theme = useTheme( );
  const showRowsPerPage = useMediaQuery(theme.breakpoints.up('md'));

  // State

  const { rowsPerPage } = preferences;
  const filterValue = typeof urlParams.filter === 'string' ? urlParams.filter : '';

  const [ page, setPage ] = useState(0);
  const [ hasBeenLoaded, setHasBeenLoaded ] = useState(false);
  const state = useListState({
    clearRecentlyModified,
    getCountAction: useCallback(( ) => getLibraryCount({
      filter: filterValue
    }), [filterValue]),
    getItemsAction: useCallback(( ) => getLibraries({
      filter: filterValue,
      skip: page * rowsPerPage,
      take: rowsPerPage
    }), [filterValue, page, rowsPerPage]),

    maxPage: libraries.count === null ? Infinity : Math.ceil(libraries.count / rowsPerPage),
    page,
    setPage
  });

  useEffect(function updateHasBeenLoaded( ) {
    if (state === 'loaded') {
      setHasBeenLoaded(true);
    }
  }, [ state ]);

  // Methods

  function getLabelForNumberOfDisplayedRows({ from, to, count }: LabelDisplayedRowsArgs) {
    const message = messages['libraries.list.displayedRows'];
    return formatMessage(message, { from, to, count });
  }
  function handleChangePage(_: unknown, target: number) {
    dispatch(clearRecentlyModified( ));
    setPage(target);
  }
  function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
    const rows = parseInt(event.target.value, 10);
    dispatch(setRowsPerPage(rows));
    dispatch(clearRecentlyModified( ));
    setPage(0);
  }
  const onFilterValueChange = useCallback(function handleFilterValueChange(value: string | null) {
    if (value) {
      dispatch(clearRecentlyModified( ));
    }
    setUrlParam('filter', value);
  }, [dispatch, setUrlParam]);

  // Rendering

  const codesForThisPage = libraries.allCodes;
  const codesRecentlyModified = libraries.recentlyModified;
  // we're always going to be showing codesRecentlyModfied at the top, so avoid showing a role twice
  // if it's in that array but also in codesForThisPage
  const codesDeduplicated = codesForThisPage.filter((code) => !codesRecentlyModified.includes(code));
  // actual number of libraries shown on this page
  const numLibraries = codesDeduplicated.length + codesRecentlyModified.length;

  const isLoadingInitially = state === 'loading' && !hasBeenLoaded;
  // show a full-page loading spinner if the list hasn't been loaded yet
  // avoids the list showing, then being removed to show the splash screen
  if (isLoadingInitially) {
    return (
      <Container maxWidth="sm" style={{ textAlign: 'center' }}>
        <CircularProgress color="inherit" />
      </Container>
    );
  }
  // show a splash screen prompting the user to create a new library if they have none in their account
  const isEmptyAccount = state === 'loaded' && !filterValue && libraries.count === 0;
  if (isEmptyAccount) {
    return (
      <SplashScreen
        icon={IconEmpty}
        heading={formatMessage(messages['libraries.list.empty.primary'])}
        details={formatMessage(messages['libraries.list.empty.secondary'])}
      >
        <Container style={{ textAlign: 'center' }}>
          <Fab
            color="primary"
            variant="extended"
            style={{ marginTop: '1em' }}
            component={MaterialUiLink}
            to={{ pathname: '/libraries/new', state: { previousLocation: location } }}
          >
            <IconAddNew style={{ marginRight: '0.3em' }} />
            <span>{formatMessage(messages['libraries.list.empty.action'])}</span>
          </Fab>
        </Container>
      </SplashScreen>
    );
  }

  const createRowWith = (props: Omit<LibraryRowProps, 'library'>) => function createRowFor(code: string) {
    const library = libraries.byCode[code];
    if (library == null) {
      throw new Error(`Library missing from state: "${code}"`);
    }
    return (<LibraryRow key={code} library={library} {...props} />);
  };
  const tableBody = state === 'loading' ? (
    <TableRow>
      <TableCell align="center" colSpan={6}>
        <CircularProgress color="inherit" />
      </TableCell>
    </TableRow>
  ) : state === 'error' ? (
    <TableRow>
      <TableCell align="center" colSpan={6}>{formatMessage(messages['libraries.list.failed'])}</TableCell>
    </TableRow>
  ) : numLibraries === 0 ? (
    <TableRow>
      <TableCell align="center" colSpan={6}>{formatMessage(messages['libraries.list.empty.primary'])}</TableCell>
    </TableRow>
  ) : [
    ...codesRecentlyModified.map(createRowWith({ isHighlighted: true })),
    ...codesDeduplicated.map(createRowWith({ isHighlighted: false }))
  ];

  return (
    <>
      <Paper>
        <Box p={2}>
          <LibrarySearch
            onFilterValueChange={onFilterValueChange}
            initialFilterValue={filterValue}
          />
        </Box>

        <Table classes={{ root: classes.tableRoot }}>
          <TableHead>
            <TableRow>
              <TableCell>{formatMessage(messages['libraries.list.name'])}</TableCell>
              <Hidden mdDown={true}>
                <TableCell>
                  {formatMessage(messages['libraries.list.description'])}
                </TableCell>
                <TableCell>{formatMessage(messages['libraries.list.created'])}</TableCell>
                <TableCell>{formatMessage(messages['libraries.list.updated'])}</TableCell>
                <TableCell>{formatMessage(messages['libraries.list.status'])}</TableCell>
              </Hidden>
              <TableCell>{formatMessage(messages['libraries.list.actions'])}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>{tableBody}</TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                labelDisplayedRows={getLabelForNumberOfDisplayedRows}
                labelRowsPerPage={formatMessage(messages['libraries.list.rowsPerPage'])}

                classes={{ spacer: classes.spacer, toolbar: classes.toolbar }}
                count={libraries.count || 0}
                page={page}

                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={showRowsPerPage ? [5, 10, 20] : []}

                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}

                ActionsComponent={TablePaginationActions}
                SelectProps={{
                  inputProps: { 'aria-label': formatMessage(messages['libraries.list.rowsPerPage']) },
                  native: true
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>

      <FloatButton
        label={formatMessage(appMessages['app.add'])}
        innerText={formatMessage(appMessages['app.add'])}
        to={{ pathname: '/libraries/new', state: { previousLocation: location } }}
      />
    </>
  );
}
export const LibrariesList = connect(
  ({ libraries, preferences }: State) => ({ libraries, preferences })
)(withQueryParams<LibrariesListProps, LibrariesListUrlParams>(LibrariesListImpl));
